import { Vector3 } from 'three';
import { GUI } from 'lil-gui';
import { updateSize } from '../utils/many-bodies';
import Stats from 'stats.js';

import icon_mag from '../../../assets/elements_html/icon_mag.png';

import icon_wind from '../../../assets/elements_html/icon_wind.png';

import icon_spacetime from '../../../assets/elements_html/icon_spacetime.png';

import { createInfoElement } from '../helpers';

export class GuiInterface {
  constructor(camera, ambientLight) {
    this.gui = new GUI();

    const guiEl = this.gui.domElement; 
    guiEl.style.position = 'absolute';
    guiEl.style.top = '0';
    guiEl.style.left = '0';
    guiEl.style.right = 'auto';

    this.camera = camera;
    this.stats = new Stats();
    this.stats.showPanel(1);
    document.body.appendChild(this.stats.dom);
    this.stats.dom.style.position = 'absolute';
    this.stats.dom.style.top = '0';
    requestAnimationFrame(() => {
      const guiWidth = guiEl.offsetWidth;
      this.stats.dom.style.left = `${guiWidth + 10}px`;
    });
    this.stats.dom.style.display = 'none';
    this.ambientLight = ambientLight;
    this.controls = {
      showStats: false,
      speedCam: 1,
      speedRotCam: 1,
      camTarget: 'earth',
      orbit: false,
      labels: false,
      spaceTimeGrid: false,
      mode: 'Orbit',
      showAsteroids: false,
      trackCamera: false,
      spectrum: true,
      timeUnit: 'Sec',
      windSun: 1,
      magnetosphere: false,
    };
    
    this.timeUnits = {
      Millisecond: 1e-8,
      Centisecond: 1e-7,
      Decisecond: 1e-6,
      Sec: 1e-5,
      Min: 0.0006,
      Hour: 0.036,
      Day: 0.864,
      Week: 6.048,
      Month: 25.92,
      Year: 315.36,
      Decade: 3153.6,
      Century: 31536.0,
      Millennium: 315360.0,
      'Ten millennia': 3153600.0,
      'Hundred millennia': 31536000.0,
      Megaannum: 315360000.0,
    };
    this.currentPos = new Vector3();
    this.camPosIni = new Vector3();
    this.prevPos = new Vector3();
    this.camControl = false;

     this.particleHighlights = [];
     this._infoContainer = null;

    this._initGlobalControls();
    this._initCameraControls();
    this._initAstroControls();
  }

  _initGlobalControls() {
    const simulationFolder = this.gui.addFolder('Simulation Parameters');

    simulationFolder
      .add(this.controls, 'showStats')
      .name('Show Stats')
      .onChange((value) => {
        this.stats.dom.style.display = value ? '' : 'none';
      });

    simulationFolder
      .add(this.controls, 'timeUnit', Object.keys(this.timeUnits))
      .name('Time Unit (per sec)')
      .onChange(() => {
        this.updateSpeeds();
        this._updateCameraModeOptions();
      });

    simulationFolder
      .add(this.ambientLight, 'intensity', 0, 2, 0.01)
      .name('Luminance');
    simulationFolder
      .addColor(this.ambientLight, 'color')
      .name('Luminance Color');
  }

  _initCameraControls() {
    const cameraFolder = this.gui.addFolder('Camera Parameters');
    const initialOptions = ['Free Camera', 'Orbit', 'Automatic'];

    this.cameraModeController = cameraFolder
      .add(this.controls, 'mode', initialOptions)
      .name('Camera Mode')
      .onChange(() => {
       this._updateCameraModeOptions();
        if (this.controls.trackCamera) {
          this.camera.getWorldPosition(this.camPosIni);
          obj.mesh.getWorldPosition(this.prevPos);
          this.camControl = true;
        }
      });

    cameraFolder
      .add(this.controls, 'trackCamera')
      .name('Tracking Camera')
      .onChange(() => {
        this.camera.getWorldPosition(this.camPosIni);
        this.camControl = true;
      });

    this.speedCamController = cameraFolder
      .add(this.controls, 'speedCam', 0, 5)
      .name('Camera Speed')
      .step(0.00001);
    this.speedRotCamController = cameraFolder
      .add(this.controls, 'speedRotCam', 0.0001, 5)
      .name('Rotate Speed')
      .step(0.0001);

    this._updateCameraModeOptions();
  }






  setInfoContainer(container) {
    if (typeof container === "string") {
      container = document.querySelector(container);
    }
    if (!container) {
      if (this._infoContainer) {
        [this.infoMag, this.infoWind, this.infoSpaceTime].forEach((el) => {
          if (el && el.parentElement) el.parentElement.removeChild(el);
        });
        this._infoContainer = null;
      }
      return;
    }

    if (container === this._infoContainer) return;

    if (this._infoContainer) {
      [this.infoMag, this.infoWind, this.infoSpaceTime].forEach((el) => {
        if (el && el.parentElement) el.parentElement.removeChild(el);
      });
    }

    container.appendChild(this.infoMag);
    container.appendChild(this.infoWind);
    container.appendChild(this.infoSpaceTime);
    this._infoContainer = container;
  }

  _initAstroControls() {
    this.astroFolder = this.gui.addFolder('Astro Parameters');
    this.groupOptions = {
      'White Hole': 'whitehole',
      'Sargitarius A*': 'sagittariusa',
      'Neutron Star': 'neutronstar',
      Sun: 'sun',
      Mercury: 'mercury',
      Venus: 'venus',
      Earth: 'earth',
      Moon: 'moon',
      Mars: 'mars',
      Jupiter: 'jupiter',
      Europa: 'europa',
      Callisto: 'callisto',
      Ganymede: 'ganymede',
      Io: 'io',
      Saturn: 'saturn',
      Mimas: 'mimas',
      Dione: 'dione',
      Iapetus: 'iapetus',
      Titan: 'titan',
      Rhea: 'rhea',
      Tethys: 'tethys',
      Uranus: 'uranus',
      Titania: 'titania',
      Oberon: 'oberon',
      Umbriel: 'umbriel',
      Ariel: 'ariel',
      Miranda: 'miranda',
      Neptune: 'neptune',
      Triton: 'triton',
      Pluto: 'pluto',
      Charon: 'charon',
      "Halley's Comet": 'halleyscomet',
      'PSO J318.5-22': 'psoj'
    };



    this.infoMag = createInfoElement("Magnetosphere", icon_mag);
    this.infoWind = createInfoElement("Solar Wind", icon_wind);
    this.infoSpaceTime = createInfoElement("Spacetime", icon_spacetime);


    this.astroFolder
      .add(this.controls, 'camTarget', this.groupOptions)
      .name('Astro')
      .listen()
      .onChange(() => {
        this.camera.getWorldPosition(this.camPosIni);
        this.camControl = true;
      });

    this.astroFolder.add(this.controls, 'orbit').name('Orbits');
    this.astroFolder.add(this.controls, 'labels').name('Labels');

    this.astroFolder
      .add(this.controls, 'spaceTimeGrid')
      .name('Spacetime')
      .onChange((value) => {
        this.infoSpaceTime.style.display = value ? 'inline-block' : 'none';  
  });

    this.astroFolder
      .add(this.controls, 'magnetosphere')
      .name('Magnetosphere')
      .onChange((value) => {
        if (!this.infoMag) return;
         this.infoMag.style.display = value ? 'inline-block' : 'none';
         this.infoWind.style.display = value ? 'inline-block' : 'none';
      });

    this.astroFolder.add(this.controls, 'spectrum').name('Full Spectrum View');

    this.astroFolder
      .add(this.controls, 'windSun', 0, 5).step(0.0001).name('Solar Wind')

       

    this.astroFolder.add(this.controls, 'showAsteroids')
      .name('Asteroid Visibility')
      .onChange(() =>this._updateAsteroidHighlights());
  }
  addParticlesHighlight(scale,particles) {
    this.particleHighlights.push({ particles, scale });
    this._updateAsteroidHighlights();
  }

  _updateAsteroidHighlights() {
    const show = this.controls.showAsteroids;
    for (const { particles, scale } of this.particleHighlights) {
      updateSize(show ? scale : 1, particles);
    }
  }

  _updateCameraModeOptions() {
    const freeMode = this.controls.mode === 'Free Camera';
    if (freeMode) {
      this.speedCamController.show();
      this.speedRotCamController.show();
    } else {
      this.speedCamController.hide();
      this.speedRotCamController.hide();
    }

    const opts = ['Free Camera', 'Orbit'];
    if (this.controls.timeUnit === 'Sec') opts.push('Automatic');
    this.cameraModeController.options(opts);
    if (!opts.includes(this.controls.mode)) {
      this.controls.mode = 'Orbit';
    }
  }

  updateSpeeds() {
    const mult = this.timeUnits[this.controls.timeUnit];
    this.controls.transSpeed = mult;
    this.controls.rotSpeed = mult;
  }

  followCam(obj) {
    if (this.controls.trackCamera) {
      obj.mesh.getWorldPosition(this.currentPos);
      if (!this.prevPos) this.prevPos = this.currentPos.clone();
      const movDelt = this.currentPos.clone().sub(this.prevPos);
      this.camera.position.add(movDelt);
      this.prevPos.copy(this.currentPos);

      if (this.camControl) {
        this.camera.position.set(
          this.camPosIni.x,
          this.camPosIni.y,
          this.camPosIni.z
        );
        this.camControl = false;
      }
      this.camera.updateMatrixWorld();
    }
  }
}
